import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CleanADirtyToteInput, CleanADirtyToteOutput } from 'src/models/cleanADirtyTote';
import RIVBFFService from 'src/services/riv-bff-service';
import { UNKNOWN_ERROR } from 'src/localization/string-ids/error-constants';
import { resetIsLoading, resetUiAlertState, setIsLoading, setUiAlertState } from 'src/store/common-slices/uiSlice';
import { AlertLevels } from 'src/models/alert-model';
import { CLEAN_A_DIRTY_CONTAINER_SUCCESS } from 'src/localization/string-ids/success-constants';
import { RIVApiMethods } from 'src/constants/api-method-constants';

export interface cleanADirtyToteState {
    toteCleanedSuccessfully: boolean;
}

const initialState: cleanADirtyToteState = {
    toteCleanedSuccessfully: false
}

export const cleanADirtyTote = createAsyncThunk<CleanADirtyToteOutput, CleanADirtyToteInput>(
    'cleanADirtyTote/cleanADirtyTote',
    async (cleanADirtyToteInput: CleanADirtyToteInput, {dispatch, rejectWithValue}) => {
        dispatch(resetUiAlertState());
        dispatch(setIsLoading());

        try {
            const cleanADirtyToteOutput = await RIVBFFService.triggerCleanADirtyToteAPI(cleanADirtyToteInput);
            dispatch(resetIsLoading());
            dispatch(setUiAlertState({
                alertLevel: AlertLevels.SUCCESS,
                alertTitleString: `${cleanADirtyToteInput.containerId}`,
                alertMessageString: CLEAN_A_DIRTY_CONTAINER_SUCCESS,
            }));
            return cleanADirtyToteOutput;

        } catch (error) {
            dispatch(resetIsLoading());

            if (error && typeof error === 'object' && 'errorMessage' in error && 'errorStatus' in error) {
                const apiError = error as { errorMessage: string; errorStatus: number };

                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${cleanADirtyToteInput.containerId}`,
                    alertMessageString: apiError.errorMessage,
                    alertStatus: apiError.errorStatus,
                    UIAction: RIVApiMethods.CLEAN_A_DIRTY_TOTE,
                    warehouse: cleanADirtyToteInput.warehouseId,
                    associate: cleanADirtyToteInput.associate,
                }));
                return rejectWithValue({
                    data: apiError.errorMessage,
                    status: apiError.errorStatus
                });
            }
            else if (error instanceof Error) {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${cleanADirtyToteInput.containerId}`,
                    alertMessageString: error.message
                }));
                return rejectWithValue(error.message);
            }
            else {
                dispatch(setUiAlertState({
                    alertLevel: AlertLevels.ERROR,
                    alertTitleString: `${cleanADirtyToteInput.containerId}`,
                    alertMessageString: UNKNOWN_ERROR
                }));
                return rejectWithValue(UNKNOWN_ERROR);
            }
        }
    }
);

const cleanADirtyToteSlice = createSlice({
    name: 'cleanADirtyTote',
    initialState,
    reducers: {
        resetCleanADirtyToteFlag: (state) => {
            state.toteCleanedSuccessfully = false;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(cleanADirtyTote.pending, (state) => {
                state.toteCleanedSuccessfully = false;
            })
            .addCase(cleanADirtyTote.fulfilled, (state) => {
                state.toteCleanedSuccessfully = true;
            })
            .addCase(cleanADirtyTote.rejected, (state) => {
                state.toteCleanedSuccessfully = false;
            })
    }
});

export const { resetCleanADirtyToteFlag } = cleanADirtyToteSlice.actions;
export default cleanADirtyToteSlice.reducer;
